/**
 * Uploading the three kinds of file this platform stores: a person's signature, the
 * organisation's stamp, and the covering letter a client sends in.
 *
 * One component rather than three because the interaction is identical — choose a file,
 * see what is on record, replace it or take it away — and because the two image cases
 * differ only in who is allowed to do it, which is the caller's business rather than
 * this component's.
 *
 * Images are fetched through `api.blob` rather than pointed at with a plain `<img src>`:
 * every one of these endpoints checks who is asking, and a bare URL in the markup would
 * arrive without the session. A signature image on an unauthenticated URL is a forgery
 * kit, and the API is right to refuse it.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ApiError, api } from "@/lib/api";
import { cn, toTitleCase } from "@/lib/format";

/**
 * An image held behind an authenticated endpoint.
 *
 * The object URL is revoked when the component unmounts or the source changes. Without
 * that, a settings screen somebody leaves open leaks a blob per refetch.
 */
export function AuthedImage({
  path,
  alt,
  className,
  refreshKey,
}: {
  path: string;
  alt: string;
  className?: string;
  /** Bump to refetch after an upload; the URL itself does not change. */
  refreshKey?: number;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;

    api
      .blob(path)
      .then((blob) => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setMissing(false);
      })
      .catch(() => {
        if (!revoked) setMissing(true);
      });

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [path, refreshKey]);

  if (missing) return null;
  if (!url) return <div className={cn("skeleton", className ?? "h-16 w-40")} />;
  return <img src={url} alt={alt} className={className} />;
}

/**
 * Choose a file, upload it, and see what is on record.
 *
 * `accept` and the size limit are advisory here and enforced by the API, which checks
 * the magic bytes rather than the extension — a file called `signature.png` is a claim,
 * not a fact.
 */
export function MediaUpload({
  title,
  hint,
  endpoint,
  imagePath,
  present,
  accept = "image/png,image/jpeg",
  canEdit,
  canRemove,
  onChanged,
  previewClassName,
  imageClassName,
}: {
  title: string;
  hint: string;
  /** POST here to upload, DELETE here to remove. */
  endpoint: string;
  /** GET here for the image itself, when one is on record. */
  imagePath: string;
  present: boolean;
  accept?: string;
  canEdit: boolean;
  canRemove: boolean;
  onChanged: () => void;
  previewClassName?: string;
  /** How the image itself sits in the preview; a round avatar wants object-cover. */
  imageClassName?: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return api.upload<{ checksum?: string | null }>(endpoint, form);
    },
    onSuccess: () => {
      setError(null);
      setVersion((current) => current + 1);
      onChanged();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "That could not be uploaded."),
  });

  const remove = useMutation({
    mutationFn: () => api.del<unknown>(endpoint),
    onSuccess: () => {
      setError(null);
      setVersion((current) => current + 1);
      onChanged();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "That could not be removed."),
  });

  return (
    <div className="space-y-2">
      <div>
        <h3 className="text-xs font-semibold">{toTitleCase(title)}</h3>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>

      {error ? (
        <p className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <div
          className={cn(
            "flex items-center justify-center rounded-md border border-dashed border-border bg-muted/40 px-3 py-2",
            previewClassName ?? "min-h-16 min-w-40",
          )}
        >
          {present ? (
            <AuthedImage
              path={imagePath}
              alt={title}
              refreshKey={version}
              className={imageClassName ?? "max-h-16 max-w-52 object-contain"}
            />
          ) : (
            <span className="text-[11px] text-muted-foreground">Nothing on record</span>
          )}
        </div>

        {canEdit ? (
          <div className="flex items-center gap-2">
            <input
              ref={input}
              type="file"
              accept={accept}
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                // Reset first: choosing the same file twice must fire again, which it
                // does not if the input still holds it.
                event.target.value = "";
                if (file) upload.mutate(file);
              }}
            />
            <button
              className="btn btn-outline"
              disabled={upload.isPending}
              onClick={() => input.current?.click()}
            >
              {upload.isPending ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Upload size={15} />
              )}
              {toTitleCase(present ? "Replace" : "Upload")}
            </button>
            {present && canRemove ? (
              <button
                className="btn btn-ghost text-destructive"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                <Trash2 size={15} /> {toTitleCase("Remove")}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export interface AttachmentItem {
  id: string;
  category: string;
  filename: string;
  size_bytes: number;
  checksum?: string | null;
  description?: string | null;
  is_locked: boolean;
  created_at: string;
}

/**
 * The files filed against a record — a request's covering letter, a sample's own
 * paperwork, an external laboratory's scope document.
 *
 * PDF by default, which the API enforces by reading the first bytes: the point of filing
 * the letter is that what is on file is the letter as the client signed it, not an
 * editable retyping of it, and a .docx would quietly defeat that. A sample's documents
 * are the exception the API also makes, and the caller says so with `accept`.
 */
export function AttachmentPanel({
  requestId,
  basePath,
  queryKey,
  category = "letter",
  uploadLabel = "Attach the request letter (PDF)",
  emptyText,
  accept = "application/pdf",
  canEdit,
  disabledReason,
  listCategories,
}: {
  /** Convenience for the original caller; equivalent to basePath on a request. */
  requestId?: string;
  /** e.g. `/v1/catalog/external-labs/{id}` — the record the files hang off. */
  basePath?: string;
  /** Cache key for this record's list. Defaults to the base path. */
  queryKey?: unknown[];
  category?: string;
  /**
   * Only these kinds are listed. For a record that files two different things, such
   * as a sample's own paperwork and its reference material's certificate, each panel
   * shows its own. Omitted, every document is listed.
   */
  listCategories?: string[];
  uploadLabel?: string;
  emptyText?: string;
  /**
   * What the file dialog offers. Advisory: the API reads the magic bytes and is the
   * one deciding what it will keep. A record whose route accepts photographs - a
   * sample's own paperwork - passes the wider list.
   */
  accept?: string;
  canEdit: boolean;
  /** Set when the record is closed and nothing more may be filed against it. */
  disabledReason?: string;
}) {
  const root = basePath ?? `/v1/requests/${requestId}`;
  const key = queryKey ?? [root, "attachments", ...(listCategories ?? [])];
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () =>
      api.get<AttachmentItem[]>(
        `${root}/attachments`,
        listCategories ? { category: listCategories } : undefined,
      ),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: key });

  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append("file", file);
      return api.upload<AttachmentItem>(`${root}/attachments`, form, { category });
    },
    onSuccess: () => {
      setError(null);
      void refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "That could not be attached."),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del<unknown>(`${root}/attachments/${id}`),
    onSuccess: () => {
      setError(null);
      void refresh();
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : "That could not be removed."),
  });

  const open = async (item: AttachmentItem) => {
    try {
      const blob = await api.blob(`${root}/attachments/${item.id}`, { inline: true });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      // Long enough for the new tab to have taken the bytes.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setError("That file could not be opened.");
    }
  };

  const items = data ?? [];

  return (
    <div className="space-y-2 p-4">
      {error ? (
        <p className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          {error}
        </p>
      ) : null}

      {isLoading ? (
        <div className="skeleton h-10" />
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {emptyText ??
            "Nothing filed yet. Most client requests arrive with a signed covering letter; regulatory and internal sampling have none."}
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {items.map((item) => (
            <li key={item.id} className="flex items-center gap-2 px-3 py-2">
              <button
                className="min-w-0 flex-1 text-left"
                onClick={() => void open(item)}
                title="Open in a new tab"
              >
                <span className="block truncate text-xs font-medium text-brand hover:underline">
                  {item.filename}
                </span>
                <span className="block truncate text-[10px] text-muted-foreground">
                  {item.category.replace(/_/g, " ")} · {Math.max(1, Math.round(item.size_bytes / 1024))} kB
                  {item.checksum ? ` · ${item.checksum.slice(0, 12)}…` : ""}
                </span>
              </button>
              {canEdit && !item.is_locked ? (
                <button
                  className="btn btn-ghost h-7 px-2 text-xs text-destructive"
                  onClick={() => remove.mutate(item.id)}
                  disabled={remove.isPending}
                  aria-label={`Remove ${item.filename}`}
                >
                  <Trash2 size={13} />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canEdit && !disabledReason ? (
        <>
          <input
            ref={input}
            type="file"
            accept={accept}
            multiple
            className="hidden"
            onChange={(event) => {
              const files = Array.from(event.target.files ?? []);
              event.target.value = "";
              for (const file of files) upload.mutate(file);
            }}
          />
          <button
            className="btn btn-outline w-full"
            disabled={upload.isPending}
            onClick={() => input.current?.click()}
          >
            {upload.isPending ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Upload size={15} />
            )}
            {toTitleCase(uploadLabel)}
          </button>
        </>
      ) : disabledReason ? (
        <p className="text-[11px] text-muted-foreground">{disabledReason}</p>
      ) : null}
    </div>
  );
}
